import { createWithEqualityFn } from "zustand/traditional";
import { isDevEnv } from "@utils/envs";

const FEATURE_FLAGS = [
  "AccessMarketplaceCreation",
  "PrebidPOC",
  "FeedExplore",
  "FeedJoinAccess",
  "Onboarding",
] as const;

export type FeatureFlags = (typeof FEATURE_FLAGS)[number];

export const getInitFeatureFlagValue = (featureName: FeatureFlags) => {
  switch (featureName) {
    case "AccessMarketplaceCreation":
      return isDevEnv();
    case "PrebidPOC":
      return false;
    case "FeedExplore":
      return isDevEnv();
    case "FeedJoinAccess":
      return isDevEnv();
    case "Onboarding":
      return true;
  }
};

type FeatureFlagReturn = {
  value: boolean;
  setValue: (_newValue: boolean) => void;
};

export const useFeatureFlag = (
  featureName: FeatureFlags,
): FeatureFlagReturn => {
  const flagStore = useFlagsStore();

  const value = flagStore.flags[featureName];
  const setValue = (newValue: boolean) => {
    flagStore.setFlag(featureName, newValue);
  };

  return { value, setValue };
};

type FeatureFlagsReturn = {
  getFlagValue: (featureName: FeatureFlags) => boolean;
  setFlagValue: (featureName: FeatureFlags, value: boolean) => void;
};

export const useFeatureFlags = (): FeatureFlagsReturn => {
  const flagStore = useFlagsStore();

  const getFlagValue = (featureName: FeatureFlags) =>
    flagStore.flags[featureName];
  const setFlagValue = (featureName: FeatureFlags, value: boolean) =>
    flagStore.setFlag(featureName, value);

  return { getFlagValue, setFlagValue };
};

const flagStoreInitialValues: FeatureFlagState = FEATURE_FLAGS.reduce(
  (acc, flag) => ({ ...acc, [flag]: getInitFeatureFlagValue(flag) }),
  {} as FeatureFlagState,
);

type FeatureFlagState = Record<FeatureFlags, boolean>;
type FeatureFlagStore = {
  flags: FeatureFlagState;
  setFlag: (featureName: string, value: boolean) => void;
};

const useFlagsStore = createWithEqualityFn<FeatureFlagStore>((set) => ({
  flags: flagStoreInitialValues,
  setFlag: (featureName, value) =>
    set((state) => {
      const newFlags = { ...state.flags, [featureName]: value };
      return { flags: newFlags };
    }),
}));

export const getFeatureFlagValue = (featureName: FeatureFlags) => {
  const flagStore = useFlagsStore.getState();
  return flagStore.flags[featureName];
};
